import React from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

/**
 * The Tarjousautomaatti look (QuoteMachineDialog), as reusable pieces: a navy icon tile next
 * to a title, numbered step headings that turn into a green check when done, white cards with
 * a 14 px radius on the near-white workspace, saturated text on a pale tint for tags, a
 * segmented control whose chosen button is a white pill, captions with a small leading icon
 * instead of stacked Alert boxes, and a thin accent focus ring instead of the app's 2 px navy
 * outline. The order builder (supplier-orders/builder/builderUi.js) and the quote machine still
 * carry their own copies of the tokens; new features import from here.
 */

// Tag colours: saturated text on a pale tint, as StatusBadge pairs its status colours.
export const TONE = {
  accent: { color: '#2b6cb0', bgcolor: '#ebf4ff', borderColor: '#bee3f8' },
  teal: { color: '#0f766e', bgcolor: '#ccfbf1', borderColor: '#99f6e4' },
  green: { color: '#2f8c54', bgcolor: '#e6ffed', borderColor: '#9ae6b4' },
  purple: { color: '#6b46c1', bgcolor: '#f5f0ff', borderColor: '#d6bcfa' },
  amber: { color: '#b45309', bgcolor: '#fef3e2', borderColor: '#fbd38d' },
  red: { color: '#c53030', bgcolor: '#fff5f5', borderColor: '#fed7d7' },
  slate: { color: '#475569', bgcolor: '#f1f5f9', borderColor: '#cbd5e1' },
};

export const CARD_SX = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '14px',
  overflow: 'hidden',
};

// A card that sits on the white page rather than the grey panel: the same shape, a hint of lift.
export const RAISED_CARD_SX = {
  ...CARD_SX,
  boxShadow: '0 1px 3px rgba(0,0,0,.05)',
};

// A focused field inside a kit dialog: a thin accent border with a soft ring.
export const FOCUS_RING_SX = {
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 3px rgba(49, 130, 206, 0.14)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main', borderWidth: 1 },
};

// A segmented control: the group is the track, the chosen button a white pill on it.
export const SEGMENT_SX = {
  bgcolor: 'background.default',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '9px',
  p: '3px',
  gap: '2px',
  flexWrap: 'wrap',
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '7px !important',
    px: 1.25,
    py: 0.5,
    gap: 0.5,
    lineHeight: 1.4,
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: 'none',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    '& .MuiSvgIcon-root': { fontSize: 15 },
    '&.Mui-selected': {
      bgcolor: 'background.paper',
      color: 'primary.main',
      boxShadow: '0 1px 2px rgba(0,0,0,.08)',
      '&:hover': { bgcolor: 'background.paper' },
    },
  },
};

// Compact inputs used inside dense rows.
export const COMPACT_INPUT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '9px', bgcolor: 'background.paper' },
  '& .MuiOutlinedInput-input': { py: 0.75, fontSize: 13 },
};

// A selectable option chip (the job menu of Tarjousautomaatti): outlined when free, filled
// navy with a check when chosen.
export const optionChipSx = (selected, { small = false } = {}) => ({
  height: small ? 30 : 34,
  fontSize: small ? 13 : 13.5,
  fontWeight: selected ? 600 : 500,
  bgcolor: selected ? 'primary.main' : 'background.paper',
  borderColor: selected ? 'primary.main' : '#cbd5e1',
  '& .MuiChip-icon': { color: 'inherit', ml: '8px', mr: '-4px' },
  ...(selected ? {} : { '&:hover': { borderColor: 'secondary.main', bgcolor: 'background.default' } }),
});

export function Overline({ children, sx, testId }) {
  return (
    <Typography
      variant="overline"
      data-testid={testId}
      sx={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1.4, color: 'text.secondary', ...sx }}
    >
      {children}
    </Typography>
  );
}

/** The navy icon tile Tarjousautomaatti puts next to its title. */
export function IconTile({ children, size = 40, tone, sx }) {
  const radius = size >= 36 ? '11px' : '8px';
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: tone ? tone.bgcolor : 'primary.main',
        color: tone ? tone.color : 'primary.contrastText',
        '& .MuiSvgIcon-root': { fontSize: Math.round(size * 0.55) },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** A 22 px tag: saturated text on a pale tint. */
export function TagChip({ label, tone = TONE.slate, icon, testId, sx }) {
  return (
    <Chip
      label={label}
      size="small"
      icon={icon}
      data-testid={testId}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: tone.color,
        bgcolor: tone.bgcolor,
        border: '1px solid',
        borderColor: tone.borderColor,
        '& .MuiChip-icon': { color: 'inherit', fontSize: 14, ml: '6px', mr: '-4px' },
        '& .MuiChip-label': { px: 1 },
        ...sx,
      }}
    />
  );
}

/** A numbered step heading; the number becomes a green check once the step is done. */
export function StepTitle({ number, title, done, aside, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap', ...sx }}>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 12, fontWeight: 700,
          bgcolor: done ? 'status.completedBg' : 'primary.main', color: done ? 'success.dark' : 'primary.contrastText',
        }}
      >
        {done ? <CheckIcon sx={{ fontSize: 15 }} /> : number}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
      <Box sx={{ flex: 1 }} />
      {aside}
    </Box>
  );
}

/** A one-line note with a small leading icon: the kit's replacement for an info Alert. */
export function Caption({ children, icon: Icon = InfoOutlinedIcon, tone = 'text.secondary', testId, sx }) {
  return (
    <Typography
      variant="caption"
      component="div"
      data-testid={testId}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, lineHeight: 1.45, color: tone, ...sx }}
    >
      {Icon && <Icon sx={{ fontSize: 15, mt: '1px', flexShrink: 0, color: tone === 'text.secondary' ? 'text.disabled' : 'inherit' }} />}
      <span>{children}</span>
    </Typography>
  );
}

/** Icon tile + title + one-line promise, with an optional close button: a dialog or page head. */
export function KitHeader({ icon, title, subtitle, onClose, actions, compact = false, sx }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, ...sx }}>
      {icon && <IconTile>{icon}</IconTile>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: compact ? 17 : { xs: 17, md: 19 }, fontWeight: 700, letterSpacing: '-.3px', lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        {subtitle && <Typography variant="body2" sx={{ mt: 0.375, lineHeight: 1.4 }}>{subtitle}</Typography>}
      </Box>
      {actions}
      {onClose && (
        <IconButton onClick={onClose} aria-label={t('Close')} sx={{ mt: -0.5, mr: -1, color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
}

/** A big figure with an overline above and a line of context under it (the price block). */
export function Figure({ overline, value, caption, testId, valueSx, sx }) {
  return (
    <Box sx={sx}>
      {overline && <Overline sx={{ mb: 0.75 }}>{overline}</Overline>}
      <Typography
        data-testid={testId}
        sx={{ fontSize: { xs: 30, md: 36 }, fontWeight: 700, letterSpacing: '-1px', lineHeight: 1, whiteSpace: 'nowrap', ...valueSx }}
      >
        {value}
      </Typography>
      {caption && <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.45 }}>{caption}</Typography>}
    </Box>
  );
}

// The dialog title row of a kit dialog: padded, with a hairline under it.
export const KIT_DIALOG_TITLE_SX = {
  display: 'block', px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 2.5 }, borderBottom: '1px solid', borderColor: 'divider',
};

// The action row of a kit dialog: padded, hairline above, buttons spaced by gap not margin.
export const KIT_DIALOG_ACTIONS_SX = {
  px: { xs: 2, md: 3.5 }, py: { xs: 1.5, md: 1.75 }, gap: 1.5, flexWrap: 'wrap',
  borderTop: '1px solid', borderColor: 'divider',
  '& > :not(style) ~ :not(style)': { ml: 0 },
};
